'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { PublicHeader } from '@/components/public/PublicHeader';
import { Footer } from '@/components/public/Footer';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';

export default function FeedbackPage() {
  const [mobileNo, setMobileNo] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [notRegistered, setNotRegistered] = useState(false);
  
  const [formData, setFormData] = useState({
    user_id: '',
    first_name: '',
    last_name: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const router = useRouter();

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mobileNo || mobileNo.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number.');
      return;
    }

    setIsVerifying(true);
    setNotRegistered(false);

    try {
      const res = await fetch(`/api/users/check?mobile=${mobileNo}`);
      const data = await res.json();

      if (data.found && data.user) {
        setIsVerified(true);
        setFormData(prev => ({
          ...prev,
          user_id: data.user.id,
          first_name: data.user.first_name || '',
          last_name: data.user.last_name || ''
        }));
      } else {
        setNotRegistered(true);
      }
    } catch (error) {
      toast.error('Failed to verify number. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      toast.error('Please enter your feedback message.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile_no: mobileNo,
          user_id: formData.user_id ? parseInt(formData.user_id) : undefined,
          first_name: formData.first_name,
          last_name: formData.last_name,
          message: formData.message
        })
      });

      if (res.ok) {
        setIsSubmitted(true);
        toast.success('Your feedback has been submitted successfully.');
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      toast.error('Could not submit your feedback. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <PublicHeader />
      
      <main className="flex-1 container max-w-3xl mx-auto py-12 px-4 sm:px-6">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Provide Feedback</h1>
          <p className="text-slate-600">We value your opinion and use it to improve our services.</p>
        </div>

        {isSubmitted ? (
          <Card className="border-t-4 border-t-green-500 shadow-lg">
            <CardContent className="pt-10 pb-10 flex flex-col items-center text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mb-6" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
              <p className="text-slate-600 mb-6 max-w-md">
                Your feedback has been successfully submitted. We appreciate your time and effort in helping us improve.
              </p>
              <Button onClick={() => router.push('/')} variant="outline">
                Return to Home
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border-t-4 border-t-amber-600">
            <CardHeader className="bg-amber-50 border-b border-amber-100 pb-6">
              <CardTitle className="text-amber-900 text-xl">Feedback Form</CardTitle>
              <CardDescription className="text-amber-700/80">
                {!isVerified ? 'Please verify your mobile number first' : 'Please provide your details and feedback'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {!isVerified ? (
                <form onSubmit={handleVerify} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="mobileNo">Mobile Number (10 digits)</Label>
                    <Input 
                      id="mobileNo" 
                      type="text" 
                      maxLength={10}
                      placeholder="e.g. 9876543210" 
                      value={mobileNo}
                      onChange={(e) => setMobileNo(e.target.value.replace(/\D/g, ''))}
                      disabled={isVerifying}
                      className="text-lg py-6"
                    />
                  </div>
                  
                  {notRegistered && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md flex flex-col gap-3">
                      <p className="font-medium">Mobile number not found in our records.</p>
                      <p className="text-sm">You must be a registered user to submit feedback automatically. You can either register now or provide your name manually.</p>
                      <div className="flex gap-3 mt-2">
                        <Button type="button" variant="outline" className="bg-white" onClick={() => router.push('/registration')}>
                          Register Now
                        </Button>
                        <Button type="button" variant="default" onClick={() => {
                          setIsVerified(true);
                          setNotRegistered(false);
                        }}>
                          Continue Manually
                        </Button>
                      </div>
                    </div>
                  )}

                  {!notRegistered && (
                    <Button type="submit" className="w-full py-6 text-lg" disabled={isVerifying || mobileNo.length !== 10}>
                      {isVerifying ? (
                        <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Verifying...</>
                      ) : (
                        'Verify Number'
                      )}
                    </Button>
                  )}
                </form>
              ) : (
                <form onSubmit={handleSubmitFeedback} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input 
                        id="firstName" 
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        required
                        disabled={!!formData.user_id}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input 
                        id="lastName" 
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        required
                        disabled={!!formData.user_id}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="mobileNoConfirm">Mobile Number</Label>
                    <Input 
                      id="mobileNoConfirm" 
                      value={mobileNo}
                      disabled
                      className="bg-slate-100"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Your Feedback</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Please share your thoughts, suggestions, or concerns..." 
                      className="min-h-[150px] resize-y"
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="pt-4 flex gap-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setIsVerified(false)}
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                      ) : (
                        'Submit Feedback'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </main>
      
      <Footer />
    </div>
  );
}
